import {
  ChangeDetectorRef,
  Component,
  HostListener,
  inject,
  InjectionToken,
  isDevMode,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { Params, Router, RouterLink } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { ENVIRONMENT_CONFIGURATION } from '@softwarehaus/util-angular-configuration';
import Mmenu from 'mmenu-js';
import { BehaviorSubject, merge, Subscription } from 'rxjs';
/* eslint-disable @nx/enforce-module-boundaries */
import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { DxButtonModule } from 'devextreme-angular/ui/button';
import { DxLoadIndicatorModule } from 'devextreme-angular/ui/load-indicator';
import { confirm } from 'devextreme/ui/dialog';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { NappEnvironmentConfiguration } from '~/app/config/environments-configuration';
import packageJson from '../../../../../../package.json';
import { MediaDirectoryService } from '../../features/document/services/mediadirectory.service';
import { CMSService } from '../../features/site/services/cms.service';
import { AuthService } from '../auth/services/authentication.service';
import { PermissionService } from '../auth/services/permission.service';
import { MessageService, TOASTTYPE } from '../services/message.service';
import { LayoutService } from '../services/store/layout.service';

enum NavigationItemType {
  Title = 'Title',
}

export interface IModulePath {
  path: string;
  queryParams?: Params;
}

export interface IModuleActions {
  value: 'add' | 'edit' | 'delete' | 'open' | 'orderUp' | 'orderDown';
  name: string;
  icon: string;
  scopes?: string[];
  message?: string;
  params?: Params;
}

type IModuleActionsFactory = (item: MenuItem) => IModuleActions;

export class MenuItem {
  public title = '';
  public moduleEnabled = false;
  public id?: string;
  public type?: NavigationItemType;
  public childs?: MenuItem[];
  public modulePath?: IModulePath;
  public requiredScopes?: string[];
  public siteActions?: IModuleActions[];
  public classNames?: string;
}

export const SIDEBAR_LEFT_REDRAW_TIMEOUT_MS = new InjectionToken<number>('SIDEBAR_LEFT_REDRAW_TIMEOUT_MS');
export const SIDEBAR_LEFT_REDRAW_TIMEOUT_ALLOW_FAIL_NUM_TIMES = new InjectionToken<number>(
  'SIDEBAR_LEFT_REDRAW_TIMEOUT_ALLOW_FAIL_NUM_TIMES',
);

@Component({
  selector: 'app-sidebar-left',
  templateUrl: './sidebar-left.component.html',
  styleUrl: './sidebar-left.component.scss',
  imports: [DxButtonModule, DxLoadIndicatorModule, NgClass, AsyncPipe, NgTemplateOutlet, RouterLink],
})
export class SidebarLeftComponent implements OnInit, OnDestroy {
  public layoutService = inject(LayoutService);
  public authService = inject(AuthService);
  private router = inject(Router);
  private cmsService = inject(CMSService);
  private messageService = inject(MessageService);
  private permissionService = inject(PermissionService);
  private mediaDirectoryService = inject(MediaDirectoryService);
  private translate = inject(TranslateService);
  private reloadTimeout = inject(SIDEBAR_LEFT_REDRAW_TIMEOUT_MS, { optional: true });
  private reloadNumAttempts = inject(SIDEBAR_LEFT_REDRAW_TIMEOUT_ALLOW_FAIL_NUM_TIMES, { optional: true });
  private config = inject<NappEnvironmentConfiguration>(ENVIRONMENT_CONFIGURATION);
  #cd = inject(ChangeDetectorRef);

  public version = (isDevMode() ? 'DEV' : packageJson.version).split('+')[0];
  public isLoading = new BehaviorSubject<boolean>(false);
  public activeActionMenuId: string | null = null;
  public activePath: string | undefined;
  public open = false;

  public pages?: MenuItem;
  public documents?: MenuItem;
  public modules?: MenuItem;
  public dividerMenuItem: MenuItem = {
    title: '',
    classNames: 'menu-divider',
    moduleEnabled: this.config.availableModules.document,
  };
  renderMenu = true;

  private mmenu?: Mmenu;
  private subscriptions = new Subscription();
  private refreshTimeout?: ReturnType<typeof setTimeout>;
  private refreshTimeoutCount = 0;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (this.activeActionMenuId) {
      this.activeActionMenuId = null;
    }
  }

  get combinedMenu(): MenuItem[] {
    const list: MenuItem[] = [];

    if (this.modules?.childs) {
      list.push(...this.modules.childs);
    }

    list.push(this.dividerMenuItem);

    if (this.documents) {
      list.push(this.documents);
    }

    list.push(this.dividerMenuItem);

    if (this.pages?.childs) {
      list.push(...this.pages.childs);
    }

    if (this.pages?.siteActions) {
      this.pages.siteActions.forEach((action) => {
        list.push({
          title: action.name,
          classNames: 'menu-action-item',
          moduleEnabled: true,
          id: 'action-add-page',
          siteActions: [action],
        }) as unknown as MenuItem;
      });
    }

    return list;
  }

  constructor() {
    this.subscriptions.add(
      this.isLoading.subscribe((next) => {
        if (this.refreshTimeout) clearTimeout(this.refreshTimeout);
        if (next && this.refreshTimeoutCount < (this.reloadNumAttempts ?? 7)) {
          this.refreshTimeout = setTimeout(() => {
            this.refreshTimeoutCount++;
            this.layoutService.triggerLeftMenuReload.next(true);
          }, this.reloadTimeout ?? 15_000);
        } else {
          this.refreshTimeoutCount = 0;
        }
      }),
    );
  }

  ngOnInit() {
    this.subscriptions.add(
      this.layoutService.showLeftSidebarSubject.subscribe((next) => {
        this.open = next;
      }),
    );

    this.subscriptions.add(
      merge(
        this.authService.isLoggedIn().pipe(distinctUntilChanged()),
        this.layoutService.reloadLeftMenu.pipe(filter((v) => !!v)),
      ).subscribe(() => {
        this.reInitDefaults();
        this.loadCMSSites();
      }),
    );
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();

    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout);
    }
  }

  public checkPermission(scopes?: string[]) {
    return !scopes || this.permissionService.checkScopes(scopes);
  }

  public hasActions(item: MenuItem) {
    if (!item.siteActions || item.siteActions.length === 0) {
      return false;
    }
    return item.siteActions.some((action) => this.checkPermission(action.scopes));
  }

  public toggleActions(event: MouseEvent, item: MenuItem) {
    event.stopPropagation();

    const itemKey = item.id ?? item.title;
    this.activeActionMenuId = this.activeActionMenuId === itemKey ? null : itemKey;
  }

  public close() {
    this.activeActionMenuId = null;
    this.layoutService.toggleLeftSidebar(false);
  }

  public executeAction(action: IModuleActions, item: MenuItem): void {
    this.activeActionMenuId = null;
    if (action.value === 'delete') {
      const confirmation = confirm(action.message!, this.translate.instant('general.confirm_title'));
      confirmation.then((dialogResult) => {
        if (dialogResult) {
          if (item.id && item?.modulePath?.path) {
            if (item.modulePath?.path?.includes('site')) {
              this.cmsService.delete(item.id).subscribe(() => {
                this.layoutService.triggerLeftMenuReload.next(true);
                this.router.navigate(['/']);
                this.close();
              });
            } else {
              this.mediaDirectoryService.delete(item.id, { mgmt: true }).subscribe(() => {
                this.layoutService.triggerLeftMenuReload.next(true);
                this.router.navigate(['/']);
                this.close();
              });
            }
          }
        }
      });
    } else if (action.value === 'orderUp' || action.value === 'orderDown') {
      this.handleOrdering(action.value === 'orderUp', item);
    } else {
      const path = [item.modulePath?.path ?? '/mediadirectories'];
      if ((action.value === 'edit' || action.value === 'open') && item.id) path.push(item.id);
      if (action.value === 'add') {
        path.push('add');
        this.router.navigate(path, { queryParams: action.params });
      } else {
        this.router.navigate(path, { queryParams: action.params });
      }
      this.close();
    }
  }

  private handleOrdering(up: boolean, item: MenuItem) {
    if (this.pages?.childs || item.id) {
      const orderIds = this.findAllOnSameLevel(this.pages!.childs!, item);
      if (orderIds.length > 0) {
        const idx = orderIds.indexOf(item.id!);
        const newIdx = idx + (up ? -1 : 1);
        if (newIdx >= 0 && newIdx < orderIds.length) {
          [orderIds[idx], orderIds[newIdx]] = [orderIds[newIdx], orderIds[idx]];
          this.cmsService.arrange(orderIds as string[]).subscribe(() => {
            this.reInitDefaults();
            this.loadCMSSites();
            this.messageService.showToast(TOASTTYPE.SUCCESS, this.translate.instant('site.order.success'));
          });
        } else {
          this.messageService.showToast(TOASTTYPE.ERROR, this.translate.instant('site.order.canNotMove'));
        }
      }
    }
  }

  private findAllOnSameLevel(levelItems: MenuItem[], searchedItem: MenuItem): string[] {
    if (levelItems.some((i) => i.id === searchedItem.id)) {
      return levelItems.map((i) => i.id).filter((id): id is string => !!id);
    }
    for (const child of levelItems) {
      if (child.childs?.length) {
        const sub = this.findAllOnSameLevel(child.childs, searchedItem);
        if (sub.length > 0) return sub;
      }
    }
    return [];
  }

  public reInitDefaults() {
    this.pages = {
      childs: [],
      title: this.translate.instant('site.navLabel'),
      id: 'pages-root',
      moduleEnabled: true,
    };
    this.documents = {
      childs: [],
      title: this.translate.instant('documents.plural'),
      id: 'documents-root',
      moduleEnabled: this.config.availableModules.document,
    };
    this.modules = {
      title: '',
      moduleEnabled: true,
      childs: [
        {
          modulePath: { path: '/feed' },
          title: this.translate.instant('feed.navLabel'),
          moduleEnabled: this.config.availableModules.feed,
        },
        {
          modulePath: { path: '/feed', queryParams: { feedtype: 'subscribed' } },
          title: this.translate.instant('feed.myFeed'),
          requiredScopes: ['tag.subscribe.create'],
          moduleEnabled: this.config.availableModules.feed,
        },
        {
          modulePath: { path: '/employee' },
          requiredScopes: ['employee.read'],
          title: this.translate.instant('employee.singular'),
          moduleEnabled: this.config.availableModules.employees,
        },
      ],
    };
  }

  private loadCMSSites() {
    this.isLoading.next(true);
    if (this.permissionService.checkScopes(['mgmt.cmssite.read', 'cmssite.read'])) {
      const cmsActions: IModuleActions[] = [
        {
          icon: 'fas fa-sort-numeric-up',
          name: this.translate.instant('general.buttons.orderUp'),
          scopes: ['mgmt.cmssite.update'],
          value: 'orderUp',
        },
        {
          icon: 'fas fa-sort-numeric-down',
          name: this.translate.instant('general.buttons.orderDown'),
          scopes: ['mgmt.cmssite.update'],
          value: 'orderDown',
        },
        {
          icon: 'far fa-eye',
          name: this.translate.instant('general.buttons.show'),
          scopes: ['mgmt.cmssite.read', 'cmssite.read'],
          value: 'open',
        },
        {
          icon: 'fas fa-pen',
          name: this.translate.instant('general.buttons.edit'),
          scopes: ['mgmt.cmssite.update', 'cmssite.update'],
          value: 'edit',
        },
        {
          icon: 'fas fa-trash',
          message: this.translate.instant('site.confirmDeletion'),
          name: this.translate.instant('general.buttons.delete'),
          scopes: ['mgmt.cmssite.delete', 'cmssite.delete'],
          value: 'delete',
        },
      ];

      this.cmsService.fetchAll({ serviceUrlConfig: { customPath: ['cms-site-navigation'] } }).subscribe((result) => {
        if (this.pages) {
          this.pages = {
            ...this.pages,
            childs: this.addChildSiteActions(this.createMenuTree(result || [], 'site', true), cmsActions),
            siteActions: this.permissionService.checkScopes(['mgmt.cmssite.read', 'cmssite.read'])
              ? [
                  {
                    icon: 'fas fa-plus',
                    name: this.translate.instant('site.addSite'),
                    scopes: ['mgmt.cmssite.create', 'cmssite.create'],
                    value: 'add',
                  },
                ]
              : [],
          };

          this.loadDirectoryChildren();
        }
      });
    } else {
      this.loadDirectoryChildren();
    }
  }

  private loadDirectoryChildren() {
    if (this.permissionService.checkScopes(['mgmt.mediadirectory.read', 'mediadirectory.read'])) {
      const actions: (IModuleActions | IModuleActionsFactory)[] = [
        {
          icon: 'far fa-eye',
          name: this.translate.instant('documents.seeDocuments'),
          scopes: ['mgmt.mediadirectory.read', 'mediadirectory.read'],
          value: 'open',
        },
        (item: MenuItem) => ({
          icon: 'fas fa-plus',
          name: this.translate.instant('directory.newDirectory'),
          scopes: ['mgmt.mediadirectory.create'],
          value: 'add',
          params: { parentId: item.id },
        }),
        {
          icon: 'fas fa-pen',
          name: this.translate.instant('directory.editDirectory'),
          scopes: ['mgmt.mediadirectory.update'],
          value: 'edit',
        },
        {
          icon: 'fas fa-trash',
          message: this.translate.instant('directory.confirmDeletion'),
          name: this.translate.instant('directory.deleteDirectory'),
          scopes: ['mgmt.mediadirectory.delete'],
          value: 'delete',
        },
      ];

      this.mediaDirectoryService.fetchAll().subscribe((results) => {
        if (this.documents) {
          this.documents = {
            ...this.documents,
            childs: this.addChildSiteActions(
              this.createMenuTree(results, 'mediadirectories', this.config.availableModules.document),
              actions,
            ),
            siteActions: this.permissionService.checkScopes(['mgmt.mediadirectory.create'])
              ? [
                  {
                    icon: 'fas fa-plus',
                    name: this.translate.instant('directory.newDirectory'),
                    scopes: ['mgmt.mediadirectory.create'],
                    value: 'add',
                  },
                ]
              : [],
          };
        }
        this.initMenu();
      });
    } else {
      this.initMenu();
    }
  }

  private initMenu() {
    this.isLoading.next(false);
    this.renderMenu = false;
    this.#cd.detectChanges();

    setTimeout(() => {
      this.renderMenu = true;
      this.#cd.detectChanges();

      this.mmenu = new Mmenu('#left-menu', {
        slidingSubmenus: true,
        offCanvas: { use: false },
      });
    }, 0);
  }

  private addChildSiteActions(children: MenuItem[], siteActions: Array<IModuleActions | IModuleActionsFactory>) {
    children.forEach((child) => {
      siteActions.forEach((actionOrFunction) => {
        const action = typeof actionOrFunction === 'function' ? actionOrFunction(child) : actionOrFunction;
        if (action.scopes && this.permissionService.checkScopes(action.scopes)) {
          child.siteActions = [...(child.siteActions || []), action];
        }
      });
      if (child.childs?.length) this.addChildSiteActions(child.childs, siteActions);
    });
    return children;
  }

  private createMenuTree(
    objects: { parent?: { id?: string }; id?: string; navtitleTranslated?: string; key?: string }[],
    path: string,
    enabled: boolean,
  ): MenuItem[] {
    if (!objects || objects.length === 0) return [];
    const filtered = objects.filter((obj) => !obj.parent || !obj.parent.id);
    return filtered.map((obj) => this.mapToMenuItem(obj, objects, path, enabled));
  }

  private mapToMenuItem(
    obj: { navtitleTranslated?: string; id?: string; key?: string; parent?: { id?: string } },
    all: { parent?: { id?: string }; id?: string; navtitleTranslated?: string; key?: string }[],
    path: string,
    enabled: boolean,
  ): MenuItem {
    const item = new MenuItem();
    item.title = obj.navtitleTranslated || obj.id || '';
    item.id = obj.id;
    item.moduleEnabled = enabled;
    item.modulePath = { path: `/${path}`, ...(obj.key ? { queryParams: { key: encodeURI(obj.key) } } : {}) };
    item.childs = all
      .filter((child) => child.parent && child.parent.id === obj.id)
      .map((child) => this.mapToMenuItem(child, all, path, enabled));
    return item;
  }
}
